import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { apiAdmin, getApiErrorMessage, getSplashData } from "../../services/api";
import { Language } from "../../App";
import { IoClose } from "react-icons/io5";
import { GoCheckCircle } from "react-icons/go";
import { HiOutlineMoon } from "react-icons/hi";
import { PiFilePdf } from "react-icons/pi";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { FaRegCircleCheck } from "react-icons/fa6";
import { FaArrowRight } from "react-icons/fa";
import { Clock3DIcon, Users3DIcon } from "../agenda/common/Agenda3DIcons";

function RadioOptionRow({
  icon,
  title,
  sub,
  selected,
  variant = "green",
  isLast = false,
  onClick,
}) {
  const isGreen = variant === "green";
  return (
    <div
      onClick={onClick}
      className="d-flex align-items-center justify-content-between"
      style={{
        border: selected
          ? `1.5px solid ${isGreen ? "#22C55E" : "#EF4444"}`
          : "1.5px solid #E2E8F0",
        background: selected
          ? isGreen
            ? "linear-gradient(145deg, #F0FDF4 0%, #DCFCE7 100%)"
            : "linear-gradient(145deg, #FEF2F2 0%, #FEE2E2 100%)"
          : "#FFFFFF",
        borderRadius: "16px",
        padding: "12px 16px",
        cursor: "pointer",
        boxShadow: selected
          ? `0 4px 14px ${isGreen ? "rgba(34, 197, 94, 0.16)" : "rgba(239, 68, 68, 0.16)"}`
          : "0 1px 3px rgba(0, 0, 0, 0.02)",
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        marginBottom: isLast ? 0 : "10px",
      }}
    >
      <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
        <span
          className="d-flex align-items-center justify-content-center flex-shrink-0"
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "10px",
            background: selected ? "#FFFFFF" : "#F8FAFC",
            border: `1px solid ${selected ? (isGreen ? "#86EFAC" : "#FECACA") : "#E2E8F0"}`,
            color: selected ? (isGreen ? "#15803D" : "#DC2626") : "#64748B",
            boxShadow: selected
              ? `0 2px 6px ${isGreen ? "rgba(21, 128, 61, 0.12)" : "rgba(220, 38, 38, 0.12)"}`
              : "none",
            transition: "all 0.2s ease",
          }}
        >
          {icon}
        </span>
        <div style={{ minWidth: 0 }}>
          <span
            className="d-block text-truncate"
            style={{
              color: selected ? (isGreen ? "#14532D" : "#991B1B") : "#0F1A2B",
              fontWeight: 750,
              fontSize: "14px",
              lineHeight: 1.25,
            }}
          >
            {title}
          </span>
          <span
            className="d-block text-truncate"
            style={{
              color: selected ? (isGreen ? "#166534" : "#B91C1C") : "#64748B",
              fontWeight: 500,
              fontSize: "12px",
              marginTop: "2px",
            }}
          >
            {sub}
          </span>
        </div>
      </div>
      <span
        className="d-flex align-items-center justify-content-center flex-shrink-0"
        style={{
          width: "20px",
          height: "20px",
          borderRadius: "50%",
          border: `2px solid ${selected ? (isGreen ? "#16A34A" : "#DC2626") : "#CBD5E1"}`,
          background: "#FFFFFF",
          transition: "all 0.2s ease",
        }}
      >
        {selected && (
          <span
            style={{
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              background: isGreen ? "#16A34A" : "#DC2626",
              boxShadow: `0 1px 4px ${isGreen ? "rgba(22, 163, 74, 0.4)" : "rgba(220, 38, 38, 0.4)"}`,
            }}
          />
        )}
      </span>
    </div>
  );
}

function ToggleSwitch({ checked, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex-shrink-0 position-relative"
      style={{
        width: "44px",
        height: "24px",
        borderRadius: "999px",
        background: checked
          ? "linear-gradient(135deg, #00C8E8 0%, #00BADA 100%)"
          : "#CBD5E1",
        boxShadow: checked
          ? "0 2px 8px rgba(0, 186, 218, 0.35)"
          : "none",
        padding: "2px",
        border: "none",
        cursor: "pointer",
        transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <span
        style={{
          display: "block",
          width: "20px",
          height: "20px",
          borderRadius: "50%",
          background: "#FFFFFF",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.15)",
          transform: checked ? "translateX(20px)" : "translateX(0)",
          transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      />
    </button>
  );
}

// Same mentor_profile the login response carries
function getMentorProfileSettings() {
  try {
    const splashProfile = JSON.parse(localStorage.getItem("SplashData"))?.user?.mentor_profile;
    const signupProfile = JSON.parse(localStorage.getItem("SignupData"))?.data?.mentor_profile;
    const profile = splashProfile || signupProfile;
    if (!profile) return null;
    return {
      isReceiveData: profile.is_receive_data === 1,
      receiveDataTitle: profile.receive_data_title || "",
      availability: profile.available === 0 ? "unavailable" : "available",
    };
  } catch (e) {
    return null;
  }
}

function buildSettingsState(settings, lang) {
  return {
    availability: settings?.availability ?? "available",
    receiveCv: settings?.isReceiveData ?? (localStorage.getItem("mr_receive_cv_enabled") === "true"),
    cvSectionTitle:
      settings?.isReceiveData && settings.receiveDataTitle
        ? settings.receiveDataTitle
        : lang?.mentorship_upload_your_cv_default || "UPLOAD YOUR CV",
  };
}

function MentorSetting({ isOpen, toggle }) {
  const { lang } = useContext(Language);
  const mentorProfileSettings = getMentorProfileSettings();
  const [availability, setAvailability] = useState(() => buildSettingsState(mentorProfileSettings, lang).availability);
  const [receiveCv, setReceiveCv] = useState(() => buildSettingsState(mentorProfileSettings, lang).receiveCv);
  const [cvSectionTitle, setCvSectionTitle] = useState(() => buildSettingsState(mentorProfileSettings, lang).cvSectionTitle);
  const [saving, setSaving] = useState(false);
  const [baseline, setBaseline] = useState(() => buildSettingsState(mentorProfileSettings, lang));

  useEffect(() => {
    if (!isOpen) return;
    const settings = buildSettingsState(getMentorProfileSettings(), lang);
    setAvailability(settings.availability);
    setReceiveCv(settings.receiveCv);
    setCvSectionTitle(settings.cvSectionTitle);
    setBaseline(settings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const hasChanges =
    availability !== baseline.availability ||
    receiveCv !== baseline.receiveCv ||
    (receiveCv && cvSectionTitle !== baseline.cvSectionTitle);

  const [isClosing, setIsClosing] = useState(false);
  const closeThen = (callback) => {
    setIsClosing(true);
    setTimeout(() => {
      toggle();
      setIsClosing(false);
      callback?.();
    }, 800);
  };
  const handleClose = () => closeThen();

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") handleClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSave = async () => {
    localStorage.setItem("mr_receive_cv_enabled", String(receiveCv));

    setSaving(true);
    try {
      const res = await apiAdmin.post("user/mentor/update", {
        available: availability === "available" ? 1 : 0,
        is_receive_data: receiveCv ? 1 : 0,
        receive_data_title: cvSectionTitle,
      });
      if (res.data?.status) {
        toast.success(lang?.mentorship_settings_saved_toast || "Your mentor settings have been saved.");
        try {
          const splashRes = await getSplashData();
          if (splashRes.data?.status) {
            localStorage.setItem("SplashData", JSON.stringify(splashRes.data));
          }
        } catch (splashErr) {
          // Non-fatal
        }
        setBaseline({ availability, receiveCv, cvSectionTitle });
        closeThen();
      } else {
        toast.error(getApiErrorMessage(res.data, lang?.mentorship_error_save_settings || "Couldn't save your mentor settings."));
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err.response?.data, lang?.mentorship_error_save_settings || "Couldn't save your mentor settings."));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen && !isClosing) return null;

  return (
    <div
      className={`ms-curtain-backdrop ${isClosing ? "closing" : ""}`}
      onClick={handleClose}
    >
      <style>{`
        /* Consistent curtain-modal pattern as ApplyMentorShip & MentorShipSchedule */
        .ms-curtain-backdrop {
          position: fixed;
          inset: 0;
          background-color: rgba(0, 0, 0, 0.5);
          z-index: 1050;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          overflow-y: auto;
          animation: msBackdropFadeIn 0.8s ease-out forwards;
        }
        .ms-curtain-backdrop.closing {
          animation: msBackdropFadeOut 0.8s ease-out forwards;
        }
        .ms-curtain-content {
          position: relative;
          width: 100%;
          max-width: 550px;
          margin: auto;
          background: #FFFFFF;
          border: 0.5px solid #E6E7EA;
          border-radius: 26px;
          max-height: 88vh;
          overflow-y: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          transform: translateY(-100vh);
          animation: msCurtainOpen 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          font-family: 'Onest', 'Outfit', 'Roboto', -apple-system, sans-serif;
        }
        .ms-curtain-content::-webkit-scrollbar {
          display: none;
          width: 0;
          height: 0;
        }
        .ms-curtain-content.closing {
          animation: msCurtainClose 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }
        @keyframes msBackdropFadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes msBackdropFadeOut {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }
        @keyframes msCurtainOpen {
          0% { transform: translateY(-100vh); }
          100% { transform: translateY(0); }
        }
        @keyframes msCurtainClose {
          0% { transform: translateY(0); }
          100% { transform: translateY(-100vh); }
        }
        @media (prefers-reduced-motion: reduce) {
          .ms-curtain-backdrop, .ms-curtain-content {
            animation: none !important;
          }
        }

        /* Modern Glass Close Button */
        .ms-close-btn {
          position: absolute;
          top: 18px;
          right: 20px;
          width: 34px;
          height: 34px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border: 1px solid #BCEAF2;
          box-shadow: 0 2px 8px rgba(0, 186, 218, 0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #0E7490;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 30;
        }
        .ms-close-btn:hover {
          background: #00BADA;
          color: #FFFFFF;
          border-color: #00BADA;
          transform: rotate(90deg) scale(1.05);
          box-shadow: 0 4px 12px rgba(0, 186, 218, 0.35);
        }

        /* CTA Button */
        .ms-cta-btn {
          width: 100%;
          height: 48px;
          border-radius: 999px;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-size: 15px;
          font-weight: 750;
          letter-spacing: 0.2px;
          color: #FFFFFF;
          background: linear-gradient(135deg, #00C8E8 0%, #00BADA 50%, #0088B3 100%);
          box-shadow: 0 6px 22px rgba(0, 186, 218, 0.4);
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          font-family: 'Onest', 'Outfit', 'Roboto', -apple-system, sans-serif;
        }
        .ms-cta-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #00DDF8 0%, #00C8E8 50%, #0099C8 100%);
          box-shadow: 0 8px 26px rgba(0, 186, 218, 0.55);
          transform: translateY(-1.5px);
        }
        .ms-cta-btn:active:not(:disabled) {
          transform: translateY(0.5px);
          box-shadow: 0 4px 14px rgba(0, 186, 218, 0.28);
        }
        .ms-cta-btn:disabled {
          opacity: 0.55;
          cursor: not-allowed;
          box-shadow: none;
        }
      `}</style>
      <div
        className={`ms-curtain-content ${isClosing ? "closing" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="position-relative" style={{ padding: "26px 24px 28px" }}>
          {/* Close button */}
          <button
            type="button"
            onClick={handleClose}
            className="ms-close-btn"
            aria-label="Close modal"
          >
            <IoClose size={18} />
          </button>

          {/* Header */}
          <h5
            style={{
              fontWeight: 800,
              fontSize: "21px",
              color: "#0F1A2B",
              margin: "0 0 4px",
              paddingRight: "36px",
              lineHeight: 1.25,
              letterSpacing: "-0.02em",
            }}
          >
            {lang?.mentorship_settings_title || "Mentor Settings"}
          </h5>
          <p
            style={{
              color: "#475569",
              fontSize: "12.5px",
              fontWeight: 500,
              margin: "0 0 18px",
              lineHeight: 1.5,
            }}
          >
            {lang?.mentorship_settings_subtitle || "Manage your availability and mentorship preferences."}
          </p>

          {/* SECTION 1: SET AVAILABILITY */}
          <div className="mb-4">
            <div className="d-flex align-items-center gap-2 mb-1">
              <Clock3DIcon size={16} />
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: 800,
                  color: "#0891B2",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                {lang?.mentorship_set_availability_title || "SET AVAILABILITY"}
              </span>
            </div>
            <p
              style={{
                color: "#64748B",
                fontSize: "12px",
                fontWeight: 500,
                margin: "0 0 10px",
                lineHeight: 1.4,
              }}
            >
              {lang?.mentorship_set_availability_sub || "Control when you're open for new sessions."}
            </p>

            <RadioOptionRow
              icon={<GoCheckCircle size={18} />}
              title={lang?.mentorship_available_title || "Available"}
              sub={lang?.mentorship_available_sub || "Open to new mentee sessions"}
              selected={availability === "available"}
              variant="green"
              onClick={() => setAvailability("available")}
            />
            <RadioOptionRow
              icon={<HiOutlineMoon size={18} />}
              title={lang?.mentorship_not_available_title || "Not available"}
              sub={lang?.mentorship_not_available_sub || "On leave or fully booked"}
              selected={availability === "unavailable"}
              variant="rose"
              isLast
              onClick={() => setAvailability("unavailable")}
            />
          </div>

          {/* SECTION 2: APPLICATION SETTINGS */}
          <div className="mb-4">
            <div className="d-flex align-items-center gap-2 mb-1">
              <Users3DIcon size={16} />
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: 800,
                  color: "#0891B2",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                {lang?.mentorship_app_settings_title || "APPLICATION SETTINGS"}
              </span>
            </div>
            <p
              style={{
                color: "#64748B",
                fontSize: "12px",
                fontWeight: 500,
                margin: "0 0 10px",
                lineHeight: 1.4,
              }}
            >
              {lang?.mentorship_app_settings_sub || "Manage how mentees submit mentorship requests."}
            </p>

            {/* Receive CV card */}
            <div
              className="d-flex align-items-start justify-content-between gap-3"
              style={{
                background: "linear-gradient(145deg, #F0FBFD 0%, #E3F6FA 100%)",
                border: "1.5px solid #B4E8F2",
                borderRadius: "16px",
                padding: "12px 16px",
                boxShadow: "0 2px 8px rgba(0, 186, 218, 0.08)",
                marginBottom: "10px",
              }}
            >
              <div
                className="d-flex align-items-start"
                style={{ minWidth: 0, gap: "10px" }}
              >
                <span
                  className="d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "10px",
                    background: "#FFFFFF",
                    border: "1px solid #B4E8F2",
                    color: "#0891B2",
                    boxShadow: "0 2px 6px rgba(0, 186, 218, 0.12)",
                    marginTop: "1px",
                  }}
                >
                  <PiFilePdf size={20} />
                </span>
                <div style={{ minWidth: 0 }}>
                  <span
                    className="d-block text-truncate"
                    style={{ color: "#0F1A2B", fontWeight: 750, fontSize: "13.5px", lineHeight: 1.3 }}
                  >
                    {lang?.mentorship_receive_cv_title || "Receive CV with Mentorship Requests"}
                  </span>
                  <span
                    className="d-block"
                    style={{
                      color: "#64748B",
                      fontWeight: 500,
                      fontSize: "11.5px",
                      lineHeight: "15px",
                      marginTop: "3px",
                    }}
                  >
                    {lang?.mentorship_receive_cv_desc ||
                      "Require mentees to upload their CV when submitting a mentorship request. This helps you review their professional background before accepting the request."}
                  </span>
                </div>
              </div>
              <div style={{ marginTop: "2px" }} className="flex-shrink-0">
                <ToggleSwitch checked={receiveCv} onChange={setReceiveCv} />
              </div>
            </div>

            {/* Section title input */}
            {receiveCv && (
              <div style={{ marginBottom: "10px" }}>
                <span
                  className="d-block mb-1"
                  style={{ color: "#334155", fontWeight: 700, fontSize: "12px" }}
                >
                  {lang?.section_title || "Section Title"}
                </span>
                <input
                  type="text"
                  value={cvSectionTitle}
                  onChange={(e) => setCvSectionTitle(e.target.value)}
                  maxLength={20}
                  placeholder={lang?.mentorship_upload_your_cv_default || "UPLOAD YOUR CV"}
                  style={{
                    width: "100%",
                    border: "1.5px solid #DCE5EE",
                    borderRadius: "12px",
                    padding: "9px 14px",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#0F1A2B",
                    outline: "none",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
                    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#00BADA";
                    e.target.style.boxShadow = "0 0 0 3px rgba(0, 186, 218, 0.15)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#DCE5EE";
                    e.target.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.03)";
                  }}
                />
              </div>
            )}

            {/* Info note box */}
            <div
              className="d-flex align-items-center gap-2"
              style={{
                background: "linear-gradient(145deg, #F0FBFD 0%, #E3F6FA 100%)",
                border: "1.5px solid #B4E8F2",
                borderRadius: "12px",
                padding: "9px 12px",
              }}
            >
              <span
                className="d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ color: "#0891B2" }}
              >
                <IoMdInformationCircleOutline size={16} />
              </span>
              <p
                className="mb-0"
                style={{
                  color: "#0891B2",
                  fontSize: "11px",
                  lineHeight: 1.35,
                  fontWeight: 550,
                }}
              >
                {lang?.mentorship_cv_info_desc ||
                  "When enabled, mentees must upload their CV while submitting a mentorship request."}
              </p>
            </div>
          </div>

          {/* Save Changes Button */}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="ms-cta-btn"
          >
            <FaRegCircleCheck size={17} />
            <span>{saving ? lang?.saving || "Saving…" : lang?.save_changes || "Save Changes"}</span>
            {!saving && <FaArrowRight size={13} />}
          </button>
        </div>
      </div>
    </div>
  );
}

export default React.memo(MentorSetting);
